import { Injectable } from '@angular/core';
import { CastMeasurementDisplay } from '@app/core/interfaces';
import { CAST_MEASUREMENTS, OPTIONAL_MEASUREMENTS } from '@core/constant/cast-measurements';
import { CastCharacterMeasureMapping as MEASUREMENT_MAP } from '@core/enums';
import { DatePipe } from '@angular/common';
import * as _ from 'lodash';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { keys, LocalStorageService } from '@app/core/services/storage/local-storage.service';

@Injectable({
    providedIn: 'root'
})
export class AppUtils {
    private pipe = new DatePipe('en-US');

    constructor(private localService: LocalStorageService) { }

    /**
     * Update cast measurement value
     * @param {Object} measurements
     * @returns {Array} measurements object with updated value
     */
    mapMeasurements(measurement) {
        return CAST_MEASUREMENTS.map((e) => ({
            ...e,
            value: measurement[e.key],
            editValue: measurement[e.key]
        }));
    }

    /**
     * Creates a cast details object
     * @param {Object} cast details
     * @returns {Object} new cast details
     */
    mapCastDetails(cast) {
        return {
            name: `${cast.legalName.firstName} ${cast.legalName.lastName}`,
            pernr: cast.pernr,
            orgUnit: cast.orgUnit,
            site: cast.site,
            siteCode: cast.siteCode,
            draft: cast.draft || 0,
            role: cast.role,
            employeeType: cast.employeeType,
            gender: cast.gender,
            status: cast.status,
            avatar: cast.mediaUrl,
            scanId: cast.scanId,
            scanLocation: cast.scanLocation,
            scanDateTime: this.pipe.transform(cast.scanDate, 'MM/dd/y hh:mm a'),
            previousScanDates: [],
            regionId: cast.regionId,
            hasEissHeight: cast.hasEissHeight,
            knownAs: cast.knownAs
        };
    }

    /**
     * Creates a cast details object
     * @param {Object} cast details
     * @param {Array} scan dates
     * @returns {Object} new cast details
     */
    mapCastPreviousScanDates(cast: any, scanDates: any) {
        return {
            ...cast,
            previousScanDates: scanDates
        };
    }

    /**
     * Get cast measurements, return as new object
     * @param {String} category
     * @param {Array} measurements
     */
    getCastMeasurements(category, measurements) {
        if (category === 'body') { return measurements.filter((e) => e.category === 'body'); }
        if (category === 'head') { return measurements.filter((e) => e.category === 'head'); }
        if (category === 'hand') { return measurements.filter((e) => e.category === 'hand'); }

        return measurements.filter((e) => e.category === 'other');
    }

    /**
     * Get formatted dates using previous scan dates, sort by descending
     * @param {Array} previous scan dates
     * @returns {Array} formatted dates
     */
    getFormattedDates(prevScanDates) {
        return prevScanDates
            .sort((a, b) => b - a)
            .map((e) => {
                return this.pipe.transform(e, 'MM/dd/y hh:mm a');
            });
    }

    /**
     * Creates a new set of scan dates
     * @param {Array} formatted dates
     * @param {Array} previous scan dates
     * @returns {Array} scan dates
     */
    getScanDates(formattedDates, prevScanDates) {
        return formattedDates.map((e, i) => (
            {
                formattedDate: e,
                unformattedDate: prevScanDates[i]
            }
        ));
    }

    /**
     * Get measurements and values
     * @param {Array} measurements
     * @returns {Array} measurement key and editValue
     */
    getMeasurementValues(arr) {
        return arr.map(({ key, editValue }) => {
            const obj = {};
            obj[`${key}`] = editValue ? editValue.toString() : '0.00';

            return obj;
        });
    }

    /**
     * Get additional measurement properties
     * @returns {Object} additional fixed properties
     */
    getAdditionalMeasurements() {
        return Object.assign({
            'scan_criteria': 'TEST'
        }, OPTIONAL_MEASUREMENTS);
    }

    /**
     * Remove element with a property of
     * @param {Array} measurements
     */
    removeElementWithProps(arr, props) {
        return arr.filter((e) => {
            // Removes fingertip because it's unknown field in db
            if (Object.prototype.hasOwnProperty.call(e, props)) { return false; }

            return true;
        });
    }

    /**
     * Get character key measurements and encode for url parameter
     * @param {CastMeasurementDisplay[]} measurements
     * @returns {string}
     */
    encodeCharacterKeyMeasurements(measurements: CastMeasurementDisplay[]): string {
        const result = {};
        measurements.forEach(item => {
            const value = item.value ?? '0';
            result[item.key] = value.toString();
        });
        const keyMeasurements = [
            'waist',
            'bust_or_chest',
            'shoulder_circumference',
            'height_in',
            'eiss_height',
            'hips_at_seat',
            'cb_neck_to_wrist_not_bent',
            'bicep_not_flexed_1',
            'cb_neck_to_waist',
            'cb_neck_to_floor',
            'thigh',
            'half_girth',
            'full_girth',
            'r_shoe_size',
            'shoulder_to_wrist_right',
            'hand_length',
            'neck_length'
        ];

        return encodeURI(JSON.stringify(_.pick(result, keyMeasurements)));
    }

    /**
     * Convert form group into cast measurements
     * @param {any} form
     * @param {CastMeasurementDisplay[]} measurements
     * @return {CastMeasurementDisplay[]}
     */
    formGroupToMeasurements(form: any, measurements: CastMeasurementDisplay[]): CastMeasurementDisplay[] {
        const formKeys = Object.keys(form);

        return measurements.map(item => {
            if (formKeys.includes(item.key)) {
                item.editValue = form[item.key] || 0.00;
            } else {
                item.editValue = 0.00;
            }

            return item;
        });
    }

    /**
     * Convert form group into cast measurements
     * @param {CastMeasurementDisplay[]} measurements
     * @return {FormGroup}
     */
    measurementsToFormGroup(measurements: CastMeasurementDisplay[], isNewForm = true): FormGroup {
        let requiredKeys = Object.keys(MEASUREMENT_MAP);

        measurements.forEach(measurement => {
            if (measurement.category === 'other' && measurement.key === 'eiss_height'
            && measurement.hasEissHeight) {
                const addEissHeightKey = { 0: 'eiss_height' };
                requiredKeys = Object.assign(requiredKeys, addEissHeightKey);
            }
        });

        const group: any = {};
        measurements.forEach(item => {
            item.isRequired = requiredKeys.includes(item.key);
            const isNewFormCondition = (isNewForm ? item.value : item.editValue) ?? 0.00;
            group[item.key] = item.isRequired
                ? new FormControl(isNewFormCondition, [Validators.required, Validators.min(1)])
                : new FormControl(isNewFormCondition);
        });

        return new FormGroup(group);
    }

    dateIsValid(date) {
        if (
            typeof date === 'object' &&
            date !== null &&
            typeof date.getTime === 'function' &&
            !isNaN(date)
        ) {
            return true;
        }

        return false;
    }

    getConfiguredSites(pageAbility: string, configuredAbility: any): string[] {
        let sites = [];
        if (configuredAbility.global.length > 0 && configuredAbility.global?.includes(pageAbility)) {
            const allSites = this.localService.getData(keys.sites);
            sites = allSites.map(site => site.site_code);
        } else {
            sites = configuredAbility[pageAbility] || [];
        }

        return sites;
    }
}
